import { inject, Injectable } from '@angular/core';
import { ShortNumberPipe } from '../pipes/short-number.pipe';
import { MatSnackBar } from '@angular/material/snack-bar';
import { hasFlag } from 'country-flag-icons'
import { PerformanceComparisonMetric } from '../components/my-brand-screen/types';
import { ShortDisplayValuePipe } from '../pipes/short-display-value.pipe';
import { Observable, throwError, timer, retry, MonoTypeOperatorFunction, catchError } from 'rxjs';

export type Format = '' | 'percentage' | 'shortCurrencyNumber' | 'shortNumber' | 'seconds' | 'currency' | 'shortPercentage' | 'fractionPercentage';

export type SnackBarVariants = "initial" | "success" | "error";
export type SnackBarPosition = "center" | "left" | "right";

export interface SnackBarOptions {
  variant: SnackBarVariants;
  position: SnackBarPosition;
  alwaysOpen?: boolean;
  duration?: number;
}

@Injectable({
  providedIn: 'root',
})
export class UtilService {

  snackBar = inject(MatSnackBar)

  constructor() { }

  private shortNumberPipe: ShortNumberPipe = new ShortNumberPipe();
  private shortDisplayValuePipe: ShortDisplayValuePipe = new ShortDisplayValuePipe();

  public capitalizeFirstLetter(text: string): string {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  public openSnackBar(msg: string, options?: Partial<SnackBarOptions>) {
    const position: SnackBarPosition = options?.position || "left";
    const variant: SnackBarVariants = options?.variant || "initial";
    const duration = options?.duration || 8000;
    this.snackBar.open(msg, " ", {
      duration: options?.alwaysOpen ? 0 : duration,
      panelClass: ["snackbar", variant, position]
    });
  }

  public closeSnackBar() {
    this.snackBar.dismiss();
  }

  public formatWidgetValue(value: number, format: Format, decimalPlaces = 2, minCurrencyValue = 100): string | number {
    switch (format) {
      case 'seconds':
        return `${Number(value.toFixed(1))}s`;
      case 'currency':
        return `$${Number(value.toFixed(decimalPlaces))}`;
      case 'fractionPercentage':
        return `${Number((value*100).toFixed(decimalPlaces))}%`;
      case 'shortPercentage':
        return `${Number(value.toFixed(0))}%`;
      case 'shortNumber':
        return this.shortNumberPipe.transform(value, 1);
      case 'percentage':
        if(value < 0.001){
          return '<0.001%'
        }
        return `${Number(value.toFixed(decimalPlaces))}%`;
      case 'shortCurrencyNumber':
        return this.shortDisplayValuePipe.transform(value, true, minCurrencyValue, '$');
      default:
        return value;
    }
  }

  public downloadFileFromUrl(fileUrl: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      window.location.assign(fileUrl);
      resolve();
    });
  }

  public getCountryFlagUrl(countryCode: string): string {
    const upperCaseCountryCode = countryCode.toUpperCase();
    if (hasFlag(upperCaseCountryCode)) {
      return `https://purecatamphetamine.github.io/country-flag-icons/3x2/${upperCaseCountryCode}.svg`;
    }
    return 'svg/globe.svg';
  }

  public getMetricRange(values: number[], isFullRange: boolean = false): { min: number; max: number } {
    if (isFullRange) {
      return { min: 0, max: 100 };
    }

    const validValues = values.filter(value => !isNaN(value as number) && value !== null);

    if (validValues.length > 0) {
      return {
        min: Math.min(...validValues),
        max: Math.max(...validValues)
      };
    }
    return { min: 0, max: 0 };
  }

  public getDisplayMetric(value: number, metric?: PerformanceComparisonMetric | string, fixed: boolean = false): string {
    switch (metric) {
      case PerformanceComparisonMetric.VIEWABILITY:
        return `${value.toFixed(0)}%`;
      case PerformanceComparisonMetric.ATTENTION:
        return `${value.toFixed(0)}%`;
      case PerformanceComparisonMetric.DWELL_TIME:
        return `${value.toFixed(1)}s`;
      case PerformanceComparisonMetric.REFRESH_RATE:
        return `${value.toFixed(1)}s`;
      case PerformanceComparisonMetric.CPM:
        return `$${value.toFixed(2)}`;
      case PerformanceComparisonMetric.IMPRESSIONS:
        return `${this.shortDisplayValuePipe.transform(value, fixed)}`;
      case PerformanceComparisonMetric.SPEND:
        return `${this.shortDisplayValuePipe.transform(value, fixed, 100, '$')}`;
      case PerformanceComparisonMetric.IMPRESSIONS_SHARE:
        return fixed ? `${value.toFixed(0)}%` : `${value.toFixed(2)}%`;
      case PerformanceComparisonMetric.AD_CLUTTER:
        return `${value.toFixed(1)}`;
      case PerformanceComparisonMetric.AD_DENSITY:
        return `${value.toFixed(0)}%`;
      case PerformanceComparisonMetric.EFFICIENCY:
      case PerformanceComparisonMetric.IMPACT:
        return `${value.toFixed(2)}`;
      default:
        return `${value}`;
    }
  }

  public hexToRgb(hex: string): { r: number; g: number; b: number } {
    const cleanHex = hex.replace('#', '');
    const bigint = parseInt(cleanHex, 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  }

  public formatDateToShort(dateString: string): string {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: '2-digit' };
    return date.toLocaleDateString(undefined, options);
  }

  public convertToPercentage(value: number): string {
    return Math.abs(value * 100).toFixed(0) + '%';
  }

  public formatTitles(title: string): string {
    if (!title) return title;
    const lowercaseWords = [
      'a', 'an', 'the', 'and', 'but', 'or', 'nor', 'for', 'yet', 'so',
      'as', 'at', 'by', 'for', 'in', 'of', 'off', 'on', 'per',
      'to', 'up', 'via', 'with', 'from', 'into', 'onto', 'over',
      'than', 'upon'
    ];

    if (!title.includes('_') && !title.includes(' ')) {
      return this.capitalizeFirstLetter(title);
    }
    title = title.replaceAll('_', ' ');
    return title.split(' ').map((word, index) =>{
      if (index === 0) return this.capitalizeFirstLetter(word);
      return !lowercaseWords.includes(word) ? this.capitalizeFirstLetter(word) : word;
    }).join(' ');
  }

  public getRandomFromRange(start: number, end: number): number {
    return Math.floor(Math.random() * (end - start)) + start;
  }

  private readonly AD_TYPE_LABELS: Record<string, string> = {
    dco: 'DCO Post',
    dynamic_product_ad: 'Dynamic Product Ad'
  };

  public formatAdType(adType: string): string {
    const key = adType?.toLowerCase();
    return this.AD_TYPE_LABELS[key] ?? this.formatTitles(key) + ' Post';
  }
  /**
   * Generic retry operator for RxJS observables with automatic error handling
   * @param shouldRetry Predicate function to determine if error should be retried. Returns true to retry, false to skip retry
   * @param retryCount Number of retries (default: 3)
   * @param errorMessage Error message to show in snackbar when all retries fail (default: "Oops something went wrong")
   * @param delayMs Delay in milliseconds between retries (default: 0 for immediate retry)
   * @returns RxJS operator function
   */
  public retryWithCondition<T>(
    shouldRetry: (error: any) => boolean,
    retryCount: number = 3,
    errorMessage: string = 'An error occurred. Please try again.',
    delayMs: number = 0
  ): MonoTypeOperatorFunction<T> {
    return (source: Observable<T>): Observable<T> => {
      return source.pipe(
        retry({
          count: retryCount,
          delay: (error: any, retryIndex: number) => {
            if (!shouldRetry(error)) {
              return throwError(() => error);
            }
            return delayMs > 0 ? timer(delayMs) : timer(0);
          }
        }),
        catchError((error: any) => {
          if (shouldRetry(error)) {
            this.openSnackBar(errorMessage, { variant: 'error' });
          }
          return throwError(() => error);
        })
      );
    };
  }
}
